/** Emboss — 3×3 directional gradient with grey-shift bias.
 *
 *  Standard Photoshop-style emboss: replace each pixel with the
 *  signed difference between the source value and a nearby sample
 *  in the `direction` quadrant, plus 128 (mid-grey). Highlights and
 *  shadows tilt away from the chosen direction, giving the embossed
 *  ("hammered metal") look.
 *
 *  Direction is offered as four cardinal directions rather than a
 *  continuous angle — the discrete steps map cleanly to the four
 *  off-axis 3×3 kernel orientations and avoid the rasterization
 *  artifacts diagonal-but-not-cardinal angles would introduce on a
 *  3-tap kernel. */

export type EmbossDirection = 'NE' | 'NW' | 'SE' | 'SW';

export interface EmbossValue {
  /** Strength multiplier. 0 = identity. 100 = full Photoshop-style
   *  emboss; values up to ~300 keep producing usable but harsher
   *  output. */
  amount: number;
  direction: EmbossDirection;
}

export const identityEmbossValue = (): EmbossValue => ({ amount: 0, direction: 'NE' });

export function applyEmbossPixels(source: ImageData, value: EmbossValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, value.amount);
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const k = amount / 100;
  // Sample offset for the "light direction". Negative dx/dy values
  // sample toward the upper-left, positive toward the lower-right.
  // The pixel "in the direction" gets subtracted from the source, so
  // a source pixel BRIGHTER than its NE neighbor → positive delta →
  // brighter than mid-grey output in the NE-lit emboss.
  let dx = 0;
  let dy = 0;
  switch (value.direction) {
    case 'NE':
      dx = 1;
      dy = -1;
      break;
    case 'NW':
      dx = -1;
      dy = -1;
      break;
    case 'SE':
      dx = 1;
      dy = 1;
      break;
    case 'SW':
      dx = -1;
      dy = 1;
      break;
  }
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const maxX = w - 1;
  const maxY = h - 1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const sx = x + dx < 0 ? 0 : x + dx > maxX ? maxX : x + dx;
      const sy = y + dy < 0 ? 0 : y + dy > maxY ? maxY : y + dy;
      const i = (y * w + x) * 4;
      const ni = (sy * w + sx) * 4;
      // Per-channel signed difference, scaled, biased to mid-grey.
      out[i] = 128 + k * (src[i] - src[ni]);
      out[i + 1] = 128 + k * (src[i + 1] - src[ni + 1]);
      out[i + 2] = 128 + k * (src[i + 2] - src[ni + 2]);
      out[i + 3] = src[i + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyEmbossToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: EmbossValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyEmbossPixels(source, value), 0, 0);
}
